import type { ColumnsType } from "@similarweb/pro-logic-components";

import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import {
    LocationCell,
    renderAbbrNumberWithThresholdCellFactory,
    renderPercentageCellFactory,
} from "app/pages/sales-intelligence/common-components/table-cells";
import { NO_DATA_HYPHEN } from "app/pages/sales-intelligence/constants/common";
import { MIN_RELIABLE_METRIC_VALUE } from "app/pages/sales-intelligence/constants/threshold";
import ColumnsService from "app/pages/sales-intelligence/modules/company-analysis/services/columns/factory";
import type {
    AppsDigitalAssetRecord,
    WebsitesDigitalAssetRecord,
} from "app/pages/sales-intelligence/modules/company-analysis/types/digital-assets";

import { AppCell } from "../../components/shared/AppCell/AppCell";
import { SiteCell } from "../../components/shared/SiteCell/SiteCell";

const formatNumber = abbrNumberFilter();

export const getWebsitesDigitalAssetsColumns = (totalCount: number): ColumnsType<WebsitesDigitalAssetRecord> => [
    {
        dataIndex: "domain",
        title: `${translate("si.company-analysis.digital-assets.table.columns.site")} ${totalCount ? ` (${totalCount})` : ""}`,
        sorter: ColumnsService.stringSorter("domain"),
        width: 185,
        render: (value: WebsitesDigitalAssetRecord["domain"]) => <SiteCell value={value} />,
    },
    {
        dataIndex: "topGeoCountry",
        title: translate("si.company-analysis.digital-assets.table.columns.topGeoCountry"),
        sorter: ColumnsService.countrySorter("topGeoCountry"),
        width: 185,
        render: (value: WebsitesDigitalAssetRecord["topGeoCountry"]) => <LocationCell locationCode={value} />,
    },
    {
        dataIndex: "visits",
        title: translate("si.company-analysis.digital-assets.table.columns.visits"),
        sorter: ColumnsService.numberSorter("visits"),
        width: 120,
        align: "end",
        render: (value: WebsitesDigitalAssetRecord["visits"]) =>
            renderAbbrNumberWithThresholdCellFactory({ min: MIN_RELIABLE_METRIC_VALUE })(value),
    },
    {
        dataIndex: "monthlyVisitsChangeMom",
        title: translate("si.company-analysis.digital-assets.table.columns.monthlyVisitsChangeMom"),
        sorter: ColumnsService.numberSorter("monthlyVisitsChangeMom"),
        width: 120,
        align: "end",
        render: (value: WebsitesDigitalAssetRecord["monthlyVisitsChangeMom"]) => renderPercentageCellFactory(value),
    },
];

export const getAppsDigitalAssetsColumns = (totalCount: number): ColumnsType<AppsDigitalAssetRecord> => [
    {
        dataIndex: "appName",
        title: `${translate("si.company-analysis.digital-assets.apps.table.columns.apps")}${totalCount ? ` (${totalCount})` : ""}`,
        sorter: ColumnsService.stringSorter("appName"),
        width: 250,
        render: (value: AppsDigitalAssetRecord["appName"], row) => <AppCell row={row}>{value}</AppCell>,
    },
    {
        dataIndex: "monthlyDownloads",
        title: translate("si.find_apps.table.column.title.monthly-downloads"),
        align: "end",
        sorter: ColumnsService.numberSorter("monthlyDownloads"),
        width: 150,
        render: (value: AppsDigitalAssetRecord["monthlyDownloads"]) =>
            value ? (formatNumber(value) as string) : NO_DATA_HYPHEN,
    },
    {
        dataIndex: "monthlyActiveUsers",
        title: translate("si.find_apps.table.column.title.monthly_active_users"),
        align: "end",
        sorter: ColumnsService.numberSorter("monthlyActiveUsers"),
        width: 150,
        render: (value: AppsDigitalAssetRecord["monthlyActiveUsers"]) =>
            value ? (formatNumber(value) as string) : NO_DATA_HYPHEN,
    },
];
